import React__default, { useState, useCallback, useEffect } from 'react';
import { imageLoader } from './helper.es.js';
import { pixelParse } from '../../helpers/utils.es.js';
import styled from '@emotion/styled';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const fallbackImage = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNjAiIGhlaWdodD0iMjYwIiB2aWV3Qm94PSIwIDAgMjYwIDI2MCI+CiAgICA8ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPgogICAgICAgIDxwYXRoIGZpbGw9IiNGNkY2RjYiIGQ9Ik0wIDBoMjYwdjI2MEgweiIvPgogICAgICAgIDxwYXRoIGQ9Ik0xMDQgMTA0aDU0djU0aC01NHoiLz4KICAgICAgICA8cGF0aCBmaWxsPSIjQjRCNEI0IiBkPSJNMTUxLjI1IDEwNi4yNWE0LjUgNC41IDAgMCAxIDQuNSA0LjV2NDAuNWE0LjUgNC41IDAgMCAxLTQuNSA0LjVoLTQwLjVhNC41IDQuNSAwIDAgMS00LjUtNC41di00MC41YTQuNSA0LjUgMCAwIDEgNC41LTQuNWg0MC41em0tMTMuNSAxOGwtOSAxMy41TDEyMiAxMzFsLTYuNzUgMTEuMjVoMzEuNWwtOS0xOHptLTEyLjM3NS02Ljc1YTMuMzc1IDMuMzc1IDAgMSAwIDAgNi43NSAzLjM3NSAzLjM3NSAwIDAgMCAwLTYuNzV6Ii8+CiAgICA8L2c+Cjwvc3ZnPgo=";
const Image = React__default.forwardRef((props, ref) => {
  const _a = props, {
    src,
    height,
    objectFit,
    width,
    alt,
    onClick,
    borderRadius,
    thumbnail,
    hover,
    decoding = "async",
    className,
    lazy = true,
    tracking,
    fetchPriority,
    aspectRatio,
    googleImageParams
  } = _a, others = __objRest(_a, [
    "src",
    "height",
    "objectFit",
    "width",
    "alt",
    "onClick",
    "borderRadius",
    "thumbnail",
    "hover",
    "decoding",
    "className",
    "lazy",
    "tracking",
    "fetchPriority",
    "aspectRatio",
    "googleImageParams"
  ]);
  const [imgSrc, setImgSrc] = useState(imageLoader(src, googleImageParams || {}));
  const imgError = useCallback(() => {
    setImgSrc((prevImgSrc) => {
      var _a2;
      if (((_a2 = prevImgSrc.params) == null ? void 0 : _a2.format) === "webp") {
        return imageLoader(src, __spreadProps(__spreadValues({}, googleImageParams), { format: "png" }));
      } else if (!prevImgSrc.error) {
        return { src: fallbackImage, error: true };
      }
      return prevImgSrc;
    });
  }, [src]);
  useEffect(() => {
    setImgSrc(imageLoader(src, googleImageParams || {}));
  }, [src, setImgSrc, aspectRatio, JSON.stringify(googleImageParams)]);
  const imgElement = /* @__PURE__ */ React__default.createElement("img", __spreadValues({
    ref,
    style: __spreadValues({
      width: width === "auto" ? width : "100%",
      height: height ? `${pixelParse(height)}` : aspectRatio ? "100%" : "auto",
      objectFit
    }, aspectRatio ? { position: "absolute", top: "0px", left: "0px" } : {}),
    src: imgSrc.src || fallbackImage,
    loading: lazy ? "lazy" : "eager",
    hover,
    decoding,
    alt,
    fetchpriority: fetchPriority,
    onError: imgError
  }, others));
  return /* @__PURE__ */ React__default.createElement(Container, __spreadValues({
    height,
    width: width || "100%",
    onClick,
    borderRadius,
    thumbnail,
    className,
    hover,
    aspectRatio
  }, tracking), imgElement);
});
Image.displayName = "Image";
const Container = styled.div(({
  onClick,
  borderRadius,
  height,
  thumbnail,
  width,
  aspectRatio,
  hover
}) => `
  position: relative;
  display: inline-block;
  overflow: hidden;
  ${onClick ? "cursor: pointer;" : ""}
  ${borderRadius ? `border-radius: ${pixelParse(borderRadius)};` : ""}
  ${borderRadius ? "1px solid #e0e0e0;" : ""}
  ${height ? `height: ${pixelParse(height)};` : ""}
  ${width ? `width: ${pixelParse(width)};` : aspectRatio ? "width: 100%;" : ""}
  ${aspectRatio && !height ? `padding-bottom: ${100 / aspectRatio}%;` : ""}
  ${thumbnail ? ` background-image: ${`url("${thumbnail}")`};
     background-size: cover;` : ""}
  ${hover ? "transition: transform 0.3s ease 0s; &:hover { transform: scale(1.05); };" : ""}
`);

export { Image };
